import React, {useEffect, useLayoutEffect, useRef, useState} from 'react';
import { fabric } from 'fabric';
import {addRect, getRandomColor, setUpRect} from './canvas-functions';
import Grid from "@mui/material/Grid";
import { styled } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import authService from "../api-authorization/AuthorizeService";
import CardContent from "@mui/material/CardContent";
import Typography from "@mui/material/Typography";
import {Fab, FormControl, ListItem, ListItemAvatar, Radio, RadioGroup, Select} from "@mui/material";
import MenuItem from "@mui/material/MenuItem";
import Card from "@mui/material/Card";
import TextField from "@mui/material/TextField";
import {MuiColorInput} from "mui-color-input";
import Button from "@mui/material/Button";
import FormControlLabel from "@mui/material/FormControlLabel";
import Divider from "@mui/material/Divider";
import List from "@mui/material/List";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import IconButton from "@mui/material/IconButton";
import DeleteIcon from "@mui/icons-material/Delete";
import FolderIcon from '@mui/icons-material/Folder';
import Avatar from "@mui/material/Avatar";
import { flushSync } from 'react-dom';
import AddLabelComponent from "./addLabelComponent";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContentText from "@mui/material/DialogContentText";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Alert from "@mui/material/Alert";
import Stack from "@mui/material/Stack";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import { keyframes } from "@mui/system";
import Tooltip from "@mui/material/Tooltip";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import AddIcon from "@mui/icons-material/Add";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
const Item = styled(Paper)(({ theme }) => ({
    backgroundColor: theme.palette.mode === 'dark' ? '#1A2027' : '#fff',
    ...theme.typography.body2,
    padding: theme.spacing(1),
    textAlign: 'center',
    color: theme.palette.text.secondary,
}));

const getBackgroundMetrics = (canvas) => {
    const bg = canvas?.backgroundImage;
    if (!bg) return null;
    const scaleX = bg.scaleX || 1;
    const scaleY = bg.scaleY || 1;
    return {
        left: bg.left || 0,
        top: bg.top || 0,
        scaleX,
        scaleY,
        width: (bg.width || 0) * scaleX,
        height: (bg.height || 0) * scaleY
    };
};

const fitBackgroundImageToCanvas = (canvas, imgObj) => {
    const cw = canvas.width;
    const ch = canvas.height;
    const iw = imgObj.width || 1;
    const ih = imgObj.height || 1;

    const scale = Math.min(cw / iw, ch / ih);
    imgObj.set({
        originX: "left",
        originY: "top",
        scaleX: scale,
        scaleY: scale,
        left: (cw - iw * scale) / 2,
        top: (ch - ih * scale) / 2
    });
};

const clamp = (v, min, max) => Math.max(min, Math.min(max, v));
const CANVAS_HEIGHT = 624;
const CANVAS_PANEL_HEIGHT = CANVAS_HEIGHT + 10;
const REDACTION_ENTRY_NAME = "Deleted Area";
const savePulse = keyframes`
  0% { transform: scale(1); }
  35% { transform: scale(1.18); }
  100% { transform: scale(1); }
`;
const saveButtonPulse = keyframes`
  0% { transform: scale(1); box-shadow: 0 0 0 rgba(25,118,210,0); }
  40% { transform: scale(1.03); box-shadow: 0 0 0 8px rgba(25,118,210,0.18); }
  100% { transform: scale(1); box-shadow: 0 0 0 rgba(25,118,210,0); }
`;
const SMALL_BOX_MIN_WIDTH = 16;
const SMALL_BOX_MIN_HEIGHT = 16;
const SMALL_BOX_MIN_AREA = 32 * 32;
const SMALL_BOX_TOOLTIP = "Small annotation box: width < 16px, height < 16px, or area < 32x32px (1024px^2).";

const getCanvasSpaceShapeDimensions = (entry) => {
    const canvasObject = entry?.rect;
    if (!canvasObject) return null;

    if (
        canvasObject.type === "group" &&
        Array.isArray(canvasObject._objects) &&
        canvasObject._objects.length > 0
    ) {
        const shape = canvasObject._objects[0];
        const groupScaleX = canvasObject.scaleX || 1;
        const groupScaleY = canvasObject.scaleY || 1;

        if (shape?.type === "rect") {
            const width = Math.abs((shape.width || 0) * (shape.scaleX || 1) * groupScaleX);
            const height = Math.abs((shape.height || 0) * (shape.scaleY || 1) * groupScaleY);
            return { width, height };
        }

        if (shape?.type === "polygon") {
            const points = Array.isArray(shape.points) ? shape.points : [];
            if (!points.length) return null;
            const xs = points.map((p) => Number(p?.x) || 0);
            const ys = points.map((p) => Number(p?.y) || 0);
            const baseWidth = Math.max(...xs) - Math.min(...xs);
            const baseHeight = Math.max(...ys) - Math.min(...ys);
            const width = Math.abs(baseWidth * (shape.scaleX || 1) * groupScaleX);
            const height = Math.abs(baseHeight * (shape.scaleY || 1) * groupScaleY);
            return { width, height };
        }
    }

    const width = Math.abs((canvasObject.width || 0) * (canvasObject.scaleX || 1));
    const height = Math.abs((canvasObject.height || 0) * (canvasObject.scaleY || 1));
    return { width, height };
};

const getAnnotationCurrentDimensions = (entry) => {
    const canvasObject = entry?.rect;
    const canvas = canvasObject?.canvas;
    const metrics = getBackgroundMetrics(canvas);
    if (!metrics) return null;

    const canvasDims = getCanvasSpaceShapeDimensions(entry);
    if (!canvasDims) return null;

    const width = canvasDims.width / (metrics.scaleX || 1);
    const height = canvasDims.height / (metrics.scaleY || 1);
    return { width, height, area: width * height };
};

const hasSmallAnnotationWarning = (entry) => {
    if (!entry || entry.isRedaction) return false;
    const dims = getAnnotationCurrentDimensions(entry);
    if (!dims) return false;
    return (
        dims.width < SMALL_BOX_MIN_WIDTH ||
        dims.height < SMALL_BOX_MIN_HEIGHT ||
        dims.area < SMALL_BOX_MIN_AREA
    );
};

const clampObjectToImageBounds = (obj, metrics) => {
    if (!obj || !metrics) return;

    const baseW = obj.width || 0;
    const baseH = obj.height || 0;
    if (baseW <= 0 || baseH <= 0) return;

    let scaleX = obj.scaleX || 1;
    let scaleY = obj.scaleY || 1;

    // Don't allow object dimensions to exceed image dimensions.
    if (baseW * scaleX > metrics.width) {
        scaleX = metrics.width / baseW;
    }
    if (baseH * scaleY > metrics.height) {
        scaleY = metrics.height / baseH;
    }
    obj.set({ scaleX, scaleY });

    const w = baseW * (obj.scaleX || 1);
    const h = baseH * (obj.scaleY || 1);

    const minLeft = metrics.left;
    const minTop = metrics.top;
    const maxLeft = metrics.left + metrics.width - w;
    const maxTop = metrics.top + metrics.height - h;

    obj.set({
        left: clamp(obj.left ?? minLeft, minLeft, Math.max(minLeft, maxLeft)),
        top: clamp(obj.top ?? minTop, minTop, Math.max(minTop, maxTop))
    });
    obj.setCoords();
};

const mergeOverlappingBoxes = (boxes) => {
    const intersects = (a, b) =>
        a.left <= b.right &&
        a.right >= b.left &&
        a.top <= b.bottom &&
        a.bottom >= b.top;

    const merged = [];
    for (const box of boxes) {
        let current = { ...box };
        let didMerge = true;
        while (didMerge) {
            didMerge = false;
            for (let i = 0; i < merged.length; i++) {
                const other = merged[i];
                if (!intersects(current, other)) continue;
                current = {
                    left: Math.min(current.left, other.left),
                    top: Math.min(current.top, other.top),
                    right: Math.max(current.right, other.right),
                    bottom: Math.max(current.bottom, other.bottom)
                };
                merged.splice(i, 1);
                didMerge = true;
                break;
            }
        }
        merged.push(current);
    }
    return merged;
};

const keepGroupLabelReadable = (group, text, rect) => {
    if (!group || !text || !rect) return;
    const recenterText = () => {
        const rectLeft = rect.left || 0;
        const rectTop = rect.top || 0;
        const rectWidth = (rect.width || 0) * (rect.scaleX || 1);
        const rectHeight = (rect.height || 0) * (rect.scaleY || 1);
        const sx = group.scaleX || 1;
        const sy = group.scaleY || 1;
        text.set({
            scaleX: 1 / sx,
            scaleY: 1 / sy,
            left: rectLeft + rectWidth / 2,
            top: rectTop + rectHeight / 2,
            originX: "center",
            originY: "center"
        });
    };
    recenterText();
    group.on("scaling", recenterText);
    group.on("modified", recenterText);
};


const ImagePolygonDrawer = ({
    img,
    imageUrl,
    image_id,
    onSave,
    brand_id,
    onDeleted,
    onMoved,
    onDirtyChange,
    registerSaveHandler,
    registerMoveHandler,
    registerDeleteHandler,
    registerRedactHandler
}) => {


    //const [isSpacePressed, setIsSpacePressed] = useState(false);


    /*(const handleKeyDown = (e) => {
        if (e.code === "Space") {
            setIsSpacePressed(true);
        }
    };

    const handleKeyUp = (e) => {
        if (e.code === "Space") {
            setIsSpacePressed(false);
        }
    };*/
    const [loading, setLoading] = React.useState(true);
    const [hasUnsavedChanges, setHasUnsavedChanges] = React.useState(false);
    const markDirty = () => setHasUnsavedChanges((prev) => (prev ? prev : true));

    const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
    const [confirmRedactionOpen, setConfirmRedactionOpen] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [deleteError, setDeleteError] = useState("");
    const [confirmMoveOpen, setConfirmMoveOpen] = useState(false);
    const [moving, setMoving] = useState(false);
    const [moveError, setMoveError] = useState("");
    const [brandOptions, setBrandOptions] = useState([]);
    const [targetBrandId, setTargetBrandId] = useState("");
    const [newLabelOpen, setNewLabelOpen] = useState(false);
    const [saveIconPulse, setSaveIconPulse] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const savePulseTimerRef = useRef(null);
    const redactionConfirmResolverRef = useRef(null);
    
    //window.addEventListener('keydown', handleKeyDown);
    //window.addEventListener('keyup', handleKeyUp);
    const getRandomByte = () => Math.floor(Math.random() * 256);
    const [labels, setLabels] = useState([
        //{"id":3, "name":"Logo 1", "c": `rgba(${getRandomByte()}, ${getRandomByte()}, ${getRandomByte()}, 0.6)`},
        //{"id":4, "name":"Logo 2", "c": `rgba(${getRandomByte()}, ${getRandomByte()}, ${getRandomByte()}, 0.6)`},
        //{"id":5, "name":"Logo 3","c": `rgba(${getRandomByte()}, ${getRandomByte()}, ${getRandomByte()}, 0.6)`},
    ]);
    const labelsRef = useRef([]);
    const [rects, setRects] = useState([]);
    
    const [currentCanvasObject, setCurrentCanvasObject] = useState(null);
    const [editingRect, setEditingRect] = useState(null);

    const canvasRef = useRef(null);
    const canvasWrapRef = useRef(null);
    const fabricRef = useRef(null);
    const rectsRef = useRef([]); // Create a reference to the rects array



    const [labelName, setLabelName] = useState("");
    const handleNameChange = (e) => {
        //setLabelName(e.target.value)
    };

    let isPanning = false;
    let lastPosX = 0;
    let lastPosY = 0;

    // --- Custom polygon state ----------------
    const [mode, setMode] = useState('rect');
    const modeRef = useRef('rect');
    const [points, setPoints] = useState([]);            // collected vertices
    const polylineRef = useRef(null);                   // live preview

    const refreshBackgroundImage = async (cacheBust = false) => {
        const canvas = fabricRef.current;
        if (!canvas) return;
        const sourceUrl = cacheBust
            ? `${imageUrl}${imageUrl.includes("?") ? "&" : "?"}v=${Date.now()}`
            : imageUrl;

        await new Promise((resolve) => {
            fabric.Image.fromURL(sourceUrl, (imgObj) => {
                fitBackgroundImageToCanvas(canvas, imgObj);
                canvas.setBackgroundImage(imgObj, () => {
                    canvas.renderAll();
                    resolve();
                });
            });
        });
    };

    const triggerSavePulse = () => {
        setSaveIconPulse(true);
        if (savePulseTimerRef.current) {
            clearTimeout(savePulseTimerRef.current);
        }
        savePulseTimerRef.current = setTimeout(() => setSaveIconPulse(false), 900);
    };

    const requestRedactionConfirm = () => new Promise((resolve) => {
        redactionConfirmResolverRef.current = resolve;
        setConfirmRedactionOpen(true);
    });

    const resolveRedactionConfirm = (confirmed) => {
        setConfirmRedactionOpen(false);
        if (redactionConfirmResolverRef.current) {
            redactionConfirmResolverRef.current(confirmed);
            redactionConfirmResolverRef.current = null;
        }
    };
    
    const makeSetup = async () => {

        if (fabricRef.current) {
            fabricRef.current.dispose();
        }

        
        fabricRef.current = new fabric.Canvas(canvasRef.current, {
            height: CANVAS_HEIGHT,
            width: 1000,
            backgroundColor: '#c0c0c0',
        });
        fabricRef.current.uniformScaling = false;
        fabricRef.current.selection = false;

        const BASE_RADIUS      = 4;
        const BASE_STROKE_WIDTH = 2;

        fabricRef.current.on('mouse:down', function(opt) {
            var e = opt.e;
            // Check if an object is active (selected)
            if (fabricRef.current.getActiveObject() == null) {
                // If no object is active, enable panning
                isPanning = true;
                lastPosX = e.clientX;
                lastPosY = e.clientY;
            }
            const zoom   = fabricRef.current.getZoom();
            if (modeRef.current !== 'custom') return;
            const pointer = fabricRef.current.getPointer(opt.e);
            // vertex marker
            // make the circle, but shrink it by 1/zoom
            const marker = new fabric.Circle({
                left:       pointer.x - BASE_RADIUS  / zoom,
                top:        pointer.y - BASE_RADIUS  / zoom,
                radius:     BASE_RADIUS  / zoom,
                fill:       'red',
                stroke:     'black',
                strokeWidth: BASE_STROKE_WIDTH / zoom,
                selectable: false
            });
            // store for later
            marker.baseRadius      = BASE_RADIUS;
            marker.baseStrokeWidth = BASE_STROKE_WIDTH;

            fabricRef.current.add(marker);

            setPoints(oldPoints => {
                const newPoints = [...oldPoints, { x: pointer.x, y: pointer.y }];

                // 4) remove old preview and draw a new one
                if (polylineRef.current) {
                    fabricRef.current.remove(polylineRef.current);
                }
                const BASE_STROKE = 2;
                const preview = new fabric.Polyline(newPoints, {
                    stroke:       'red',
                    strokeWidth:  BASE_STROKE/zoom,
                    fill:         'rgba(255,0,0,0.1)',
                    selectable:   false
                });
                preview.baseStrokeWidth = BASE_STROKE;
                fabricRef.current.add(preview);
                polylineRef.current = preview;

                return newPoints;
            });

        });

        fabricRef.current.on('mouse:dblclick', function(opt) {
            const target = opt?.target;
            if (!target?.id || modeRef.current === 'custom') return;
            const entry = rectsRef.current.find(r => r.id === target.id);
            if (!entry || entry.isRedaction) return;
            const canvasElement = canvasRef.current;
            const wrapper = canvasElement?.parentElement;
            if (!canvasElement || !wrapper) return;
            const wrapperBounds = wrapper.getBoundingClientRect();
            const clickX = Number(opt?.e?.clientX);
            const clickY = Number(opt?.e?.clientY);
            const overlayWidth = 210;
            setEditingRect({
                id: target.id,
                // Use the browser event position rather than Fabric's
                // untransformed bounds; this remains correct after zoom/pan.
                left: Math.max(0, (Number.isFinite(clickX) ? clickX : wrapperBounds.left) - wrapperBounds.left - overlayWidth / 2),
                top: Math.max(0, (Number.isFinite(clickY) ? clickY : wrapperBounds.top) - wrapperBounds.top - 17),
                width: overlayWidth,
                labelId: entry.label?.id
            });
        });

        fabricRef.current.on('mouse:up', function() {
            isPanning = false;
        });

        fabricRef.current.on('mouse:move', function(opt) {
            if (isPanning && opt && opt.e) {
                var e = opt.e;
                var vpt = fabricRef.current.viewportTransform;
                vpt[4] += e.clientX - lastPosX;
                vpt[5] += e.clientY - lastPosY;
                fabricRef.current.setViewportTransform(vpt);
                lastPosX = e.clientX;
                lastPosY = e.clientY;
            }
        });
        
        fabricRef.current.on('mouse:wheel', function(opt) {
            var delta = opt.e.deltaY;
            var zoom = fabricRef.current.getZoom();
            zoom *= 0.999 ** delta;
            if (zoom > 20) zoom = 20;
            if (zoom < 0.01) zoom = 0.01;
            fabricRef.current.zoomToPoint({ x: opt.e.offsetX, y: opt.e.offsetY }, zoom);
            opt.e.preventDefault();
            opt.e.stopPropagation();
        });

        fabricRef.current.on('object:moving', function(e) {
            const obj = e?.target;
            const metrics = getBackgroundMetrics(fabricRef.current);
            if (!obj || !metrics) return;
            clampObjectToImageBounds(obj, metrics);
        });

        fabricRef.current.on('object:scaling', function(e) {
            const obj = e?.target;
            const metrics = getBackgroundMetrics(fabricRef.current);
            if (!obj || !metrics) return;
            clampObjectToImageBounds(obj, metrics);
        });

        fabricRef.current.on('object:modified', () => {
            setCurrentCanvasObject(fabricRef.current.toObject().objects)
            setRects(prev => {
                const next = [...rectsRef.current];
                rectsRef.current = next;
                return next;
            });
            markDirty();
        });

       
        fabricRef.current.on('selection:created', function(e) {
            
            if(e.selected.length == 1 && (e.selected.type == 'rect' || e.selected.type == 'group')) {
                return;
            }
            var id = e.selected[0].id;
            var updatedRects = rectsRef.current.map((r) => {
                // Reset the selected property for all rectangles
                r.selected = false;
                // If the rectangle id matches the selected id, set selected to true
                if (r.id === id) {
                    r.selected = true;
                    console.log({id, r});
                }
                return r;
            });
            // Update the rects state with the new array
            setRects(updatedRects);
        });
        fabricRef.current.on('selection:cleared', function() {
            // Iterate over the rects array
            const updatedRects = rectsRef.current.map((r) => {
                // Set the selected property of each rectangle to false
                r.selected = false;
                return r;
            });

            // Update the rects state with the new array
            setRects(updatedRects);
        });

        fabricRef.current.on('selection:updated', function(e) {
            if(e.selected.length == 1 && (e.selected.type == 'rect' || e.selected.type == 'group')) {
                return;
            }
            var id = e.selected[0].id;
            var updatedRects = rectsRef.current.map((r) => {
                // Reset the selected property for all rectangles
                r.selected = false;
                // If the rectangle id matches the selected id, set selected to true
                if (r.id === id) {
                    r.selected = true;
                    console.log({id, r});
                }
                return r;
            });
            // Update the rects state with the new array
            setRects(updatedRects);
        });
        
        /*fabric.Image.fromURL(imageUrl, function(img) {
            fabricRef.current.setBackgroundImage(img, fabricRef.current.renderAll.bind(fabricRef.current), {
                left: (fabricRef.current.width/2)-(img.width/2),
                top: (fabricRef.current.height/2)-(img.height/2)
            });
            //canvas.add(img.set({ left: (canvas.width/2)-(img.width/2), top: (canvas.height/2)-(img.height/2)}));
        });*/

        await new Promise(resolve => {
            fabric.Image.fromURL(imageUrl, img => {
                fitBackgroundImageToCanvas(fabricRef.current, img);
                fabricRef.current.setBackgroundImage(img, fabricRef.current.renderAll.bind(fabricRef.current));
                resolve();
            });
        });

        
        setCurrentCanvasObject(fabricRef.current.toObject().objects);


        return () => {
            if (fabricRef.current) {
                fabricRef.current.dispose();
            }
        };
    };

    const resetPolygon = () => {
        const canvas = fabricRef.current;
        // remove the live Polyline
        if (polylineRef.current) {
            canvas.remove(polylineRef.current);
            polylineRef.current = null;
        }
        // remove all the circle markers
        canvas.getObjects('circle').forEach(c => canvas.remove(c));
        // clear your collected points
        setPoints([]);
        // switch back to rect (if you like)
        flushSync(() => setMode('rect'));
        modeRef.current = 'rect';
        // re-render
        canvas.requestRenderAll();
    };
    
    const fetchLabels = () => {

        //const token = await authService.getAccessToken();
        fetch('/api/organisation/labels/' + brand_id, {
            //headers: !token ? {} : {'X-Authorization': `Bearer ${token}`} }
        }).then((response)=> {
            authService.followIfLoginRedirect(response);
            return response.json();
        }).then(data => {
            setLabels(data);
            console.log('Data received:', data);
            loadRects(data);
        });

    }

    const finishPolygon = () => {
        const canvas = fabricRef.current;
        
        const viewportTransform = canvas.viewportTransform;
        
        if (points.length < 3) return;

        // Compute the image offset & normalized conversion
        const metrics = getBackgroundMetrics(canvas);
        if (!metrics) return;
        const zoom      = canvas.getZoom();

        const currentZoom = canvas.getZoom();
        // 1) normalize canvas to zoom 1
        canvas.setZoom(1);
        canvas.renderAll();

// 2) build normalizedPoints in *pixel* units
        const normalizedPoints = points.map(p => ({
            x: (p.x - metrics.left) / metrics.scaleX,
            y: (p.y - metrics.top) / metrics.scaleY
        }));

// 3) for drawing, just re-add the offset
        const absPoints = normalizedPoints.map(p => ({
            x: (p.x * metrics.scaleX) + metrics.left,
            y: (p.y * metrics.scaleY) + metrics.top
        }));

        // 3) Find top-left of bounding box
        const xs  = absPoints.map(p => p.x),
            ys  = absPoints.map(p => p.y),
            minX = Math.min(...xs),
            minY = Math.min(...ys);

        // 4) Build local coordinate array for Fabric.Polygon
        const localPts = absPoints.map(p => ({
            x: p.x - minX,
            y: p.y - minY
        }));

        // 5) Create the polygon exactly over your click area
        const lbl = labelsRef.current[0];
        if (!lbl) return;
        const hex   = lbl.color_hex;
        const poly  = new fabric.Polygon(localPts, {
            left:               minX,
            top:                minY,
            originX:            'left',
            originY:            'top',
            fill:               `rgba(${hex},0.3)`,
            stroke:             `rgba(${hex},1)`,
            strokeWidth:        2,
            absolutePositioned: true,
            selectable:         true
        });

        // 6) Compute centroid in canvas space for text
        const sum = absPoints.reduce((a,p) => ({ x: a.x+p.x, y: a.y+p.y }), { x:0, y:0 });
        const cx  = sum.x / absPoints.length;
        const cy  = sum.y / absPoints.length;

        // 7) Create the text label at that centroid
        const text = new fabric.Text(lbl.name, {
            left:      cx,
            top:       cy,
            originX:   'center',
            originY:   'center',
            fontSize:  15,
            selectable:false
        });

        // 8) Group them (group origin = top-left is fine)
        const group = new fabric.Group([poly, text], {
            id:        `${lbl.id}-${rectsRef.current.length}`,
            left:      minX,
            top:       minY,
            originX:   'left',
            originY:   'top'
        });

        // 9) **Store the normalized points** on the group for saving
        group.normalizedPoints = normalizedPoints;

        // 10) Add & update state
        canvas.add(group);
        rectsRef.current.push({ id: group.id, label: lbl, rect: group, selected: false });
        setRects([...rectsRef.current]);

        // 11) Clean up preview
        canvas.remove(polylineRef.current);
        canvas.getObjects('circle').forEach(c => canvas.remove(c));
        setPoints([]);
        polylineRef.current = null;

        // 12) Back to Rect mode
        flushSync(() => setMode('rect'));
        modeRef.current = 'rect';
        markDirty();

        // restore zoom/viewport
        canvas.setZoom(currentZoom);
        canvas.setViewportTransform(viewportTransform);
        canvas.requestRenderAll();
    };

    const loadRects = (allLabels) => {
        console.log('load rects');
        fetch('/api/organisation/labels/img/' + image_id + "/annotations", {
            method: "GET",
            headers: { 'Content-Type': 'application/json' },
        })
            .then(response => response.json())
            .then((annotations) => {
                const canvas = fabricRef.current;

                const metrics = getBackgroundMetrics(canvas);
                if (!metrics) {
                    setRects([]);
                    return;
                }

                const normalizePoints = (rawPoints) => {
                    if (Array.isArray(rawPoints)) return rawPoints;
                    if (typeof rawPoints === "string") {
                        try {
                            const parsed = JSON.parse(rawPoints);
                            return Array.isArray(parsed) ? parsed : [];
                        } catch {
                            return [];
                        }
                    }
                    return [];
                };

                const loaded = (annotations || []).map((ann, index) => {
                    try {
                        const labelId = Number(ann?.label_id ?? ann?.labelId ?? 0);
                        const label = allLabels.find(l => l.id === labelId) || {
                            id: labelId,
                            name: `Label #${labelId}`,
                            color_hex: "128,128,128"
                        };

                        let shapeObject, groupId;
                        const shape = (ann?.shape || "rect").toString().toLowerCase();
                        const annPoints = normalizePoints(ann?.points);

                        if (shape === "rect" || shape === "") {
                            let r1=ann;
                            let width = (r1.right-r1.left) * metrics.scaleX;
                            let height = (r1.bottom-r1.top) * metrics.scaleY;
                            let left = (r1.left * metrics.scaleX) + metrics.left;
                            let top = (r1.top * metrics.scaleY) + metrics.top;

                            const testRect = new fabric.Rect({
                                fill: 'rgba(' + label.color_hex + ', 0.4)',
                                height: height,
                                width: width,
                                stroke: 'rgba(' + label.color_hex + ', 1)',
                                strokeWidth: 2,
                                lockUniScaling: false,
                            });

                            const text = new fabric.Text(label.name, {
                                left: testRect.left + testRect.width / 2,
                                top: testRect.top + testRect.height / 2,
                                fontSize: 15,
                                originX: 'center',
                                originY: 'center',
                                fontFamily: 'sans-serif'
                            });

                            const group = new fabric.Group([testRect, text], {
                                id: label.id + "-" + index,
                                left: left,
                                top: top,
                                height: height,
                                width: width,
                            });
                            keepGroupLabelReadable(group, text, testRect);
                            fabricRef.current.add(group);
                            clampObjectToImageBounds(group, metrics);

                            return {id: label.id + "-" + index, label: label, rect: group, selected: false}
                            
                        }  else if (shape === 'polygon' && annPoints.length >= 3) {
                        // 1) build absolute points
                        const absPoints = annPoints.map(p => ({
                            x: (p.x * metrics.scaleX) + metrics.left,
                            y: (p.y * metrics.scaleY) + metrics.top
                        }));

                        // 2) bounding box
                        const xs   = absPoints.map(p => p.x);
                        const ys   = absPoints.map(p => p.y);
                        const minX = Math.min(...xs), minY = Math.min(...ys);

                        // 3) **rebase** into local coords around top-left
                        const localPointsForPoly = absPoints.map(p => ({
                            x: p.x - minX,
                            y: p.y - minY
                        }));

                        // 4) draw polygon with top-left origin
                        const hex = label.color_hex;
                        const poly = new fabric.Polygon(localPointsForPoly, {
                            left:               minX,
                            top:                minY,
                            originX:            'left',
                            originY:            'top',
                            fill:               `rgba(${hex},0.4)`,
                            stroke:             `rgba(${hex},1)`,
                            strokeWidth:        2,
                            absolutePositioned: true,
                            selectable:         true
                        });

                        // 5) draw the text at the centroid
                        const sum = absPoints.reduce((a,p) => ({ x: a.x+p.x, y: a.y+p.y }), { x:0,y:0 });
                        const cx  = sum.x / absPoints.length, cy = sum.y / absPoints.length;
                        const text = new fabric.Text(label.name, {
                            left:    cx, top:    cy,
                            originX: 'center', originY: 'center',
                            fontSize:15, selectable:false
                        });
                        

                        // 6) group them with the same top-left
                        shapeObject = new fabric.Group([poly, text], {
                            id:      `${label.id}-${index}`,
                            left:    minX, top:    minY,
                            originX: 'left', originY: 'top'
                        });
                        shapeObject.normalizedPoints = annPoints;

                        groupId = `${label.id}-${index}`;
                    }

                        if (!shapeObject) return null;
                        // Add to canvas & return our rects array entry
                        fabricRef.current.add(shapeObject);
                        return {
                            id: groupId,
                            label,
                            rect: shapeObject,
                            selected: false
                        };
                    } catch (e) {
                        console.error("Failed to load annotation", ann, e);
                        return null;
                    }
                }).filter(Boolean);

                setRects(loaded);
                rectsRef.current = loaded;
                canvas.renderAll(); // Ensure canvas is re-rendered after adding objects
            });
    };




    useEffect(() => {
        async function runEffect() {
            setHasUnsavedChanges(false);
            await makeSetup();
            fetchLabels();
        }
        runEffect();
    }, [img]);

    useEffect(() => {
        if (typeof onDirtyChange === "function") {
            onDirtyChange(hasUnsavedChanges);
        }
    }, [hasUnsavedChanges, onDirtyChange]);
    // sync modeRef when mode state changes
    useEffect(() => {
        modeRef.current = mode;
    }, [mode]);
    const addLabelById = (labelId) => {
        if (!labelId) return;
        const canvas = fabricRef.current;
        if (!canvas) return;
        const canvasWidth = fabricRef.current.width;
        const canvasHeight = fabricRef.current.height;
        const viewportTransform = canvas.viewportTransform;

// Get the viewport transform
        //const viewportTransform = fabricRef.current.viewportTransform;

// Calculate the center of the viewport
        //const viewportCenter = {
        //    x: -viewportTransform[4] + canvasWidth / 2,
        //    y: -viewportTransform[5] + canvasHeight / 2
        //};

        const inverseViewportTransform = fabric.util.invertTransform(viewportTransform);

        // Calculate the center of the viewport in canvas coordinates
        const viewportCenter = fabric.util.transformPoint({
            x: canvasWidth / 2,
            y: canvasHeight / 2
        }, inverseViewportTransform);

        const zoomLevel = canvas.getZoom();
        // Calculate the scaled dimensions
        const scaledWidth = 200 / zoomLevel;
        const scaledHeight = 200 / zoomLevel;
        
        const lbl = labelsRef.current.find(f => f.id == labelId);
        if (!lbl) return;
        const testRect = new fabric.Rect({
            //id: label.id + "-" + index,

            height:scaledHeight,
            width: scaledWidth,
            left: viewportCenter.x - scaledWidth / 2,
            top: viewportCenter.y - scaledHeight / 2,
            //left: viewportCenter.x-100,
            //top: viewportCenter.y-100,
            stroke: 'rgba(' + lbl .color_hex + ', 1)',
            fill: 'rgba(' + lbl .color_hex + ', 0.4)',
            strokeWidth: 2  / zoomLevel,
            lockUniScaling: false,
        });
        //fabricRef.current.add(testRect);
        //fabricRef.current.setActiveObject(testRect);

        const text = new fabric.Text(lbl .name, {
            left: testRect.left + testRect.width / 2,
            top: testRect.top + testRect.height / 2,
            fontSize: 15 / zoomLevel,
            originX: 'center',
            originY: 'center',
            fontFamily: 'sans-serif'
        });

// Create a group that contains the rectangle and the text
        const nextIndex = rectsRef.current.length;
        const group = new fabric.Group([testRect, text], {
            id: lbl.id + "-" + nextIndex,
            height: scaledHeight,
            width: scaledWidth,
            //left: viewportCenter.x-100,
            //top: viewportCenter.y-100,
            left: viewportCenter.x - scaledWidth / 2,
            top: viewportCenter.y - scaledHeight / 2,
        });
        keepGroupLabelReadable(group, text, testRect);
        fabricRef.current.add(group);
        const metrics = getBackgroundMetrics(fabricRef.current);
        if (metrics) {
            clampObjectToImageBounds(group, metrics);
        }
        //fabricRef.current.setActiveObject(group);

        
        
        const nextRect = {id: lbl.id + "-" + nextIndex, label: lbl, rect: group, selected: false};
        setRects(prev => {
            const next = [...prev, nextRect];
            rectsRef.current = next;
            return next;
        });
        markDirty();

        flushSync(() => setMode('rect'));
        modeRef.current = 'rect';
    };
    const addRedactionBox = () => {
        const canvas = fabricRef.current;
        if (!canvas) return;

        const viewportTransform = canvas.viewportTransform;
        const inverseViewportTransform = fabric.util.invertTransform(viewportTransform);
        const viewportCenter = fabric.util.transformPoint({
            x: canvas.width / 2,
            y: canvas.height / 2
        }, inverseViewportTransform);

        const zoomLevel = canvas.getZoom();
        const scaledWidth = 220 / zoomLevel;
        const scaledHeight = 120 / zoomLevel;

        const boxId = `redaction-${Date.now()}-${Math.floor(Math.random() * 100000)}`;
        const redactRect = new fabric.Rect({
            id: boxId,
            left: viewportCenter.x - scaledWidth / 2,
            top: viewportCenter.y - scaledHeight / 2,
            width: scaledWidth,
            height: scaledHeight,
            fill: "rgba(0,0,0,0.7)",
            stroke: "rgba(0,0,0,1)",
            strokeWidth: 2 / zoomLevel,
            selectable: true,
            isRedaction: true
        });

        canvas.add(redactRect);
        const metrics = getBackgroundMetrics(canvas);
        if (metrics) {
            clampObjectToImageBounds(redactRect, metrics);
        }

        const redactionEntry = {
            id: boxId,
            label: { name: REDACTION_ENTRY_NAME },
            rect: redactRect,
            selected: false,
            isRedaction: true
        };

        setRects((prev) => {
            const next = [...prev, redactionEntry];
            rectsRef.current = next;
            return next;
        });
        markDirty();
    };

    const handleLabelAdd= async () => {
        await fetchLabels();
        setNewLabelOpen(false);
    };
    
    const removeLabel = async (id) => {
// Get all objects from the canvas
        const allObjects = fabricRef.current.getObjects();

        // Find the rectangle with the matching id
        const rectToDelete = allObjects.find(obj => obj.id === id);

        setRects(prev => prev.filter(f => f.id !== id));
        rectsRef.current = rectsRef.current.filter(f => f.id !== id);
        // If a matching rectangle is found, remove it from the canvas
        if (rectToDelete) {
            fabricRef.current.remove(rectToDelete);
        }
        markDirty();
    };

    useEffect(() => {
        labelsRef.current = labels;
    }, [labels]);

    useEffect(() => () => {
        if (savePulseTimerRef.current) {
            clearTimeout(savePulseTimerRef.current);
        }
        if (redactionConfirmResolverRef.current) {
            redactionConfirmResolverRef.current(false);
            redactionConfirmResolverRef.current = null;
        }
    }, []);

    useEffect(() => {
        const onKeyDown = (e) => {
            const isSaveShortcut = (e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "s";
            if (isSaveShortcut) {
                e.preventDefault();
                if (isSaving || newLabelOpen || confirmDeleteOpen) return;
                triggerSavePulse();
                setIsSaving(true);
                Promise.resolve(handleSave())
                    .finally(() => setIsSaving(false));
                return;
            }

            const active = document.activeElement;
            const tag = (active?.tagName || "").toLowerCase();
            const isTypingTarget =
                tag === "input" ||
                tag === "textarea" ||
                active?.isContentEditable;
            if (isTypingTarget) return;

            if (newLabelOpen || confirmDeleteOpen) return;

            if (e.key === "Tab") {
                const allRects = rectsRef.current;
                if (!allRects.length) return;
                e.preventDefault();

                const currentIndex = allRects.findIndex(r => r.selected);
                const direction = e.shiftKey ? -1 : 1;
                const nextIndex = currentIndex === -1
                    ? 0
                    : (currentIndex + direction + allRects.length) % allRects.length;
                selectRect(null, allRects[nextIndex].id);
                return;
            }

            if (/^[1-9]$/.test(e.key)) {
                const shortcutIndex = Number(e.key) - 1;
                const shortcutLabel = labelsRef.current[shortcutIndex];
                if (!shortcutLabel) return;
                e.preventDefault();
                addLabelById(shortcutLabel.id);
                return;
            }

            if (e.key !== "Delete" && e.key !== "Backspace") return;

            const canvas = fabricRef.current;
            if (!canvas) return;

            const activeObject = canvas.getActiveObject();
            const selectedId = activeObject?.id;
            if (!selectedId) return;

            e.preventDefault();
            removeLabel(selectedId).catch(() => {});
        };

        window.addEventListener("keydown", onKeyDown);
        return () => window.removeEventListener("keydown", onKeyDown);
    }, [newLabelOpen, confirmDeleteOpen, isSaving, labels, rects]);

    const handleSave = async () => {
        const canvas = fabricRef.current;
       
        const viewportTransform = canvas.viewportTransform;
        const currentZoom = canvas.getZoom();
        // 1) normalize canvas to zoom 1
        canvas.setZoom(1);
        canvas.renderAll();

        const metrics = getBackgroundMetrics(canvas);
        if (!metrics) {
            canvas.setZoom(currentZoom);
            canvas.setViewportTransform(viewportTransform);
            canvas.requestRenderAll();
            return false;
        }
        const zoom      = canvas.getZoom(); // now 1
console.log(zoom)
        const allCanvasObjects = canvas.getObjects();
        const redactionObjects = allCanvasObjects.filter((obj) => obj?.isRedaction === true);
        const redactionBoxesRaw = redactionObjects
            .map((obj) => {
                const width = (obj.width * obj.scaleX) / zoom;
                const height = (obj.height * obj.scaleY) / zoom;
                const left = ((obj.left / zoom) - (metrics.left / zoom)) / metrics.scaleX;
                const top = ((obj.top / zoom) - (metrics.top / zoom)) / metrics.scaleY;
                const right = left + (width / metrics.scaleX);
                const bottom = top + (height / metrics.scaleY);

                return {
                    left: Math.max(0, left),
                    top: Math.max(0, top),
                    right: Math.max(0, right),
                    bottom: Math.max(0, bottom)
                };
            })
            .filter((b) => b.right > b.left && b.bottom > b.top);
        const redactionBoxes = mergeOverlappingBoxes(redactionBoxesRaw);

        if (redactionBoxes.length > 0) {
            const proceed = await requestRedactionConfirm();
            if (!proceed) {
                canvas.setZoom(currentZoom);
                canvas.setViewportTransform(viewportTransform);
                canvas.requestRenderAll();
                return false;
            }
        }

        const coords = allCanvasObjects
            .map(obj => {
                if (obj?.isRedaction === true) return null;
                // only handle your grouped shapes
                if (!obj._objects) return null;

                const base = {
                    brand_image_id: image_id,
                    label_id:      parseInt(obj.id.split('-')[0], 10)
                };

                const isRect = obj._objects[0] instanceof fabric.Rect;
                if (isRect) {
                    // rectangle: as before
                    const width  = (obj.width  * obj.scaleX) / zoom;
                    const height = (obj.height * obj.scaleY) / zoom;
                    const left   = ((obj.left  / zoom) - (metrics.left / zoom)) / metrics.scaleX;
                    const top    = ((obj.top   / zoom) - (metrics.top  / zoom)) / metrics.scaleY;
                    const normWidth = width / metrics.scaleX;
                    const normHeight = height / metrics.scaleY;

                    return {
                        ...base,
                        shape:   'rect',
                        top, left, width: normWidth, height: normHeight,
                        right:   left + normWidth,
                        bottom:  top  + normHeight,
                        points:  []
                    };
                }

                // otherwise it must be a polygon
                const poly    = obj._objects[0];              // your fabric.Polygon
                const matrix  = obj.calcTransformMatrix();    // full group transform
                const rawPts  = poly.get('points');           // [{x,y},…] in local coords

                // transform each point into canvas space
                const points = rawPts.map(p => {
                    const ptTrans = fabric.util.transformPoint(
                        new fabric.Point(p.x, p.y),
                        matrix
                    );
                    return {
                        x: ((ptTrans.x / zoom ) - (metrics.left / zoom)) / metrics.scaleX,
                        y: ((ptTrans.y /zoom ) - (metrics.top  / zoom)) / metrics.scaleY
                    };
                });

                return {
                    ...base,
                    shape:  'polygon',
                    points: obj.normalizedPoints
                };
            })
            .filter(o => o !== null);


        // restore zoom/viewport
        canvas.setZoom(currentZoom);
        canvas.setViewportTransform(viewportTransform);
        canvas.requestRenderAll();

        // POST them off
        setLoading(true);
        const response = await fetch(`/api/organisation/labels/img/${image_id}/annotations`, {
            method:  'POST',
            headers: { 'Content-Type': 'application/json' },
            body:    JSON.stringify(coords),
        });
        authService.followIfLoginRedirect(response);
        setLoading(false);
        if (!response.ok) {
            return false;
        }

        if (redactionBoxes.length > 0) {
            const redactResponse = await fetch(`/api/organisation/labels/img/${image_id}/redactions`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ boxes: redactionBoxes }),
            });
            authService.followIfLoginRedirect(redactResponse);
            if (!redactResponse.ok) {
                return false;
            }

            const removedIds = new Set(redactionObjects.map((obj) => obj.id));
            redactionObjects.forEach((obj) => canvas.remove(obj));
            setRects((prev) => {
                const next = prev.filter((r) => !removedIds.has(r.id));
                rectsRef.current = next;
                return next;
            });
            await refreshBackgroundImage(true);
        }

        setHasUnsavedChanges(false);
        if (typeof onDirtyChange === "function") {
            onDirtyChange(false);
        }
        if (onSave) onSave(image_id);
        return true;
    };

    useEffect(() => {
        if (typeof registerSaveHandler !== "function") return undefined;
        registerSaveHandler(() => async () => {
            const ok = await handleSave();
            return ok !== false;
        });
        return () => registerSaveHandler(null);
    }, [registerSaveHandler, image_id]);

    useEffect(() => {
        if (typeof registerMoveHandler !== "function") return undefined;
        registerMoveHandler(() => () => {
            setMoveError("");
            setConfirmMoveOpen(true);
        });
        return () => registerMoveHandler(null);
    }, [registerMoveHandler]);

    useEffect(() => {
        if (typeof registerDeleteHandler !== "function") return undefined;
        registerDeleteHandler(() => () => setConfirmDeleteOpen(true));
        return () => registerDeleteHandler(null);
    }, [registerDeleteHandler]);

    useEffect(() => {
        if (typeof registerRedactHandler !== "function") return undefined;
        registerRedactHandler(() => () => addRedactionBox());
        return () => registerRedactHandler(null);
    }, [registerRedactHandler]);


    const handleResetZoom = () => {
        const canvas = fabricRef.current;
        if (!canvas) return;
        // Reset both zoom and pan so the view returns to centered baseline.
        canvas.setViewportTransform([1, 0, 0, 1, 0, 0]);
        canvas.requestRenderAll();
    };

    const selectRect = (event, id) => {
        // Get all objects from the canvas
        const allObjects = fabricRef.current.getObjects();

        // Find the rectangle with the matching id
        const rectToSelect = allObjects.find(obj => obj.id === id);

        // If a matching rectangle is found, set it as the active object
        if (rectToSelect) {
            fabricRef.current.setActiveObject(rectToSelect);
            fabricRef.current.renderAll();
        }
        setRects(prev => {
            const next = prev.map(r => ({ ...r, selected: r.id === id }));
            rectsRef.current = next;
            return next;
        });
    };

    const renameAnnotation = (id, labelId) => {
        const label = labelsRef.current.find(item => Number(item.id) === Number(labelId));
        const entry = rectsRef.current.find(item => item.id === id);
        if (!label || !entry) return;
        const index = String(id).split('-').slice(1).join('-');
        const nextId = `${label.id}-${index}`;
        entry.rect.id = nextId;
        const text = entry.rect?._objects?.find(item => item.type === 'text');
        if (text) text.set({ text: label.name });
        entry.label = label;
        entry.id = nextId;
        entry.selected = true;
        rectsRef.current = [...rectsRef.current];
        setRects(rectsRef.current);
        fabricRef.current?.setActiveObject(entry.rect);
        fabricRef.current?.requestRenderAll();
        setEditingRect(null);
        markDirty();
    };

    const doDelete = async () => {
        setDeleting(true);
        setDeleteError("");
        try {
            const resp = await fetch(`/api/organisation/brands/${brand_id}/img/${image_id}`, {
                method: 'DELETE'
            });
            authService.followIfLoginRedirect(resp);
            if (resp.status === 204) {
                setConfirmDeleteOpen(false);
                // tell parent this image is gone
                if (typeof onSave === 'function') onSave(image_id); // keep existing callback use if you prefer
                if (typeof onDeleted === 'function') onDeleted(image_id);
            } else {
                const t = await resp.text();
                setDeleteError(t || `Delete failed (${resp.status})`);
            }
        } catch (e) {
            setDeleteError(e?.message || 'Delete failed');
        } finally {
            setDeleting(false);
        }
    };

    useEffect(() => {
        fetch('/api/organisation/brands', {
            method: 'GET',
            headers: { 'Content-Type': 'application/json' },
        })
            .then((response) => {
                authService.followIfLoginRedirect(response);
                return response.json();
            })
            .then((data) => {
                const source = Array.isArray(data?.brands) ? data.brands : [];
                const currentBrandId = Number(brand_id);
                const options = source.filter((b) => b.id !== currentBrandId);
                setBrandOptions(options);
                if (options.length > 0) {
                    setTargetBrandId(options[0].id);
                }
            })
            .catch(() => {
                setBrandOptions([]);
            });
    }, [brand_id]);

    const doMove = async () => {
        setMoveError("");
        if (!targetBrandId) {
            setMoveError("Choose a target brand.");
            return;
        }

        setMoving(true);
        try {
            const resp = await fetch(`/api/organisation/brands/${brand_id}/img/${image_id}/move`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ targetBrandId: Number(targetBrandId) })
            });
            authService.followIfLoginRedirect(resp);
            if (resp.status === 204) {
                setConfirmMoveOpen(false);
                if (typeof onMoved === 'function') onMoved(image_id, Number(targetBrandId));
            } else {
                const t = await resp.text();
                setMoveError(t || `Move failed (${resp.status})`);
            }
        } catch (e) {
            setMoveError(e?.message || "Move failed");
        } finally {
            setMoving(false);
        }
    };
    
    return (
        <React.Fragment>

            <Grid container spacing={0} sx={{ flex: 1, minHeight: 0, alignItems: 'flex-start' }} >
                <Grid item xs={'auto'}>

                    <Box ref={canvasWrapRef} sx={{pt:"5px", pl:"5px", position: 'relative'}}>
                        <canvas id="c" ref={canvasRef}/>
                        {editingRect && (
                            <Select
                                size="small"
                                autoFocus
                                open={Boolean(editingRect)}
                                value={String(editingRect.labelId ?? '')}
                                onChange={(event) => renameAnnotation(editingRect.id, event.target.value)}
                                onClose={() => setEditingRect(null)}
                                sx={{
                                    position: 'absolute',
                                    left: editingRect.left,
                                    top: editingRect.top,
                                    minWidth: Math.min(240, editingRect.width),
                                    height: 34,
                                    backgroundColor: 'rgba(255,255,255,0.96)',
                                    zIndex: 5
                                }}
                            >
                                {labels.map(label => (
                                    <MenuItem key={label.id} value={String(label.id)}>{label.name}</MenuItem>
                                ))}
                            </Select>
                        )}
                    </Box>

                </Grid>
                <Grid
                    item
                    xs
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        pl: 0,
                        height: `${CANVAS_PANEL_HEIGHT}px`,
                        minHeight: 0
                    }}
                >

                    <Box sx={{ width: '100%', flex: 1, minHeight: 0, overflow: 'hidden' }}>
                        <Box sx={{ p: 2, height: '100%', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
                            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                                    Add Labels
                                </Typography>
                                <Stack direction="row" spacing={0.5} alignItems="center">
                                    <Button size="small" variant="outlined" onClick={() => setNewLabelOpen(true)}>
                                        New Label
                                    </Button>
                                    <Tooltip
                                        arrow
                                        placement="left"
                                        title={
                                            <Box sx={{ py: 0.5 }}>
                                                <Typography variant="caption" sx={{ display: "block" }}>
                                                    Keyboard shortcuts
                                                </Typography>
                                                <Typography variant="caption" sx={{ display: "block" }}>
                                                    Ctrl/Cmd+S: Save
                                                </Typography>
                                                <Typography variant="caption" sx={{ display: "block" }}>
                                                    1-9: Add label 1-9
                                                </Typography>
                                                <Typography variant="caption" sx={{ display: "block" }}>
                                                    Tab / Shift+Tab: Next/prev box
                                                </Typography>
                                                <Typography variant="caption" sx={{ display: "block" }}>
                                                    Delete / Backspace: Delete selected box
                                                </Typography>
                                            </Box>
                                        }
                                    >
                                        <IconButton size="small" aria-label="Keyboard shortcuts">
                                            <HelpOutlineIcon fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                </Stack>
                            </Stack>
                            <Box sx={{ maxHeight: 190, overflowY: 'auto', overflowX: 'hidden', flexShrink: 0, pr: 0.5 }}>
                                {labels.map((label, index) => {
                                    return (
                                        <Box
                                            key={label.id}
                                            onClick={() => addLabelById(label.id)}
                                            onDoubleClick={() => addLabelById(label.id)}
                                            sx={{
                                                display: "flex",
                                                alignItems: "center",
                                                width: "100%",
                                                minWidth: 0,
                                                gap: 1,
                                                my: 0.35,
                                                px: 0.6,
                                                py: 0.35,
                                                borderRadius: 1,
                                                cursor: "pointer",
                                                "&:hover": { backgroundColor: "rgba(25,118,210,0.08)" }
                                            }}
                                        >
                                            <Typography variant="body2" sx={{ pr: 0.5, flex: 1, minWidth: 0, fontSize: 14, lineHeight: 1.2 }}>
                                                {label.name}
                                            </Typography>
                                            {index < 9 && (
                                                <Box
                                                    component="span"
                                                    sx={{
                                                        ml: "auto",
                                                        minWidth: 20,
                                                        px: 0.5,
                                                        py: 0.1,
                                                        borderRadius: 1,
                                                        border: "1px solid",
                                                        borderColor: "rgba(0,0,0,0.28)",
                                                        textAlign: "center",
                                                        fontSize: 11,
                                                        fontWeight: 700,
                                                        lineHeight: 1.2,
                                                        color: "text.primary",
                                                        background: "linear-gradient(180deg, #ffffff 0%, #f2f4f7 100%)",
                                                        boxShadow: "inset 0 1px 0 rgba(255,255,255,0.95), 0 1px 2px rgba(0,0,0,0.15)",
                                                        fontFamily: "'Roboto Mono', 'Consolas', 'Menlo', monospace"
                                                    }}
                                                >
                                                    {index + 1}
                                                </Box>
                                            )}
                                            <IconButton
                                                size="small"
                                                color="primary"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    addLabelById(label.id);
                                                }}
                                                sx={{ p: 0.45 }}
                                                aria-label={`Add ${label.name}`}
                                            >
                                                <AddIcon fontSize="inherit" />
                                            </IconButton>
                                        </Box>
                                    );
                                })}
                            </Box>

                            {rects.some((r) => r?.isRedaction) && (
                                <Alert severity="warning" sx={{ mt: 0.75, py: 0 }}>
                                    Delete areas are irreversible after Save.
                                </Alert>
                            )}

                            <Typography variant="subtitle1" sx={{ mt: 1, mb: 0.75, fontWeight: 600 }}>
                                Added Labels
                            </Typography>
                            <Box
                                sx={{
                                    flex: 1,
                                    minHeight: 0,
                                    overflowY: "auto",
                                    border: "1px solid",
                                    borderColor: "divider",
                                    borderRadius: 1
                                }}
                            >
                                <List component="nav" sx={{ p: 0, m: 0 }} aria-label="added labels">
                                    {rects.map(r => {
                                        const showSmallWarning = hasSmallAnnotationWarning(r);
                                        return (
                                            <ListItem
                                                key={r.id}
                                                selected={r.selected === true}
                                                onClick={(event) => selectRect(event, r.id)}
                                                secondaryAction={
                                                    <IconButton edge="end" aria-label="delete" onClick={() => removeLabel(r.id)}>
                                                        <DeleteIcon />
                                                    </IconButton>
                                                }
                                            >
                                                <ListItemText
                                                    primary={
                                                        <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.75 }}>
                                                            <Box component="span">{r.label.name}</Box>
                                                            {showSmallWarning && (
                                                                <Tooltip title={SMALL_BOX_TOOLTIP} arrow>
                                                                    <WarningAmberIcon
                                                                        sx={{
                                                                            color: "warning.dark",
                                                                            fontSize: 24,
                                                                            fontWeight: 900,
                                                                            lineHeight: 1,
                                                                            filter: "drop-shadow(0 0 1px rgba(0,0,0,0.35))",
                                                                            cursor: "help"
                                                                        }}
                                                                    />
                                                                </Tooltip>
                                                            )}
                                                        </Box>
                                                    }
                                                />
                                            </ListItem>
                                        );
                                    })}
                                </List>
                            </Box>
                        </Box>
                        
                    </Box>

                    <Divider sx={{ marginBottom: '20px' }} />
                    <Box sx={{ marginRight:1, marginLeft:1, display: 'flex', justifyContent: 'space-between' }} >

                        {/* Left side: utility + destructive */}
                        <Stack direction="row" spacing={1}>
                            <Button onClick={handleResetZoom} variant="outlined" color="inherit">
                                Reset Zoom
                            </Button>
                        </Stack>

                        {/* Right side: primary action */}
                        <Button
                            autoFocus
                            onClick={async () => {
                                if (isSaving) return;
                                triggerSavePulse();
                                setIsSaving(true);
                                try {
                                    await handleSave();
                                } finally {
                                    setIsSaving(false);
                                }
                            }}
                            variant="contained"
                            disabled={isSaving}
                            startIcon={
                                <SaveOutlinedIcon
                                    sx={{
                                        animation: saveIconPulse ? `${savePulse} 900ms ease-out` : "none"
                                    }}
                                />
                            }
                            sx={{
                                animation: (saveIconPulse || isSaving) ? `${saveButtonPulse} 900ms ease-out` : "none",
                                transition: "transform 120ms ease"
                            }}
                        >
                            {isSaving ? "Saving..." : "Save"}
                        </Button>
                    </Box>
                    
                </Grid>
            </Grid>
            <Dialog open={confirmDeleteOpen} onClose={() => !deleting && setConfirmDeleteOpen(false)}>
                <DialogTitle>Delete this image?</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        This will permanently delete the file and all annotations. This action cannot be undone.
                    </DialogContentText>
                    {deleteError && (
                        <Alert severity="error" sx={{ mt: 1 }}>{deleteError}</Alert>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setConfirmDeleteOpen(false)} disabled={deleting}>Cancel</Button>
                    <Button onClick={doDelete} color="error" disabled={deleting}>
                        {deleting ? 'Deleting…' : 'Delete permanently'}
                    </Button>
                </DialogActions>
            </Dialog>
            <Dialog open={confirmRedactionOpen} onClose={() => resolveRedactionConfirm(false)}>
                <DialogTitle>Apply delete areas permanently?</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        This will permanently black out selected areas in the source image. This action cannot be undone.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => resolveRedactionConfirm(false)}>Cancel</Button>
                    <Button color="error" variant="contained" onClick={() => resolveRedactionConfirm(true)}>
                        Apply permanently
                    </Button>
                </DialogActions>
            </Dialog>
            <Dialog open={confirmMoveOpen} onClose={() => !moving && setConfirmMoveOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Move image to another brand?</DialogTitle>
                <DialogContent>
                    <DialogContentText sx={{ mb: 2 }}>
                        Moving this image will remove all existing annotations for this image.
                    </DialogContentText>
                    <FormControl fullWidth size="small">
                        <Select
                            value={targetBrandId}
                            onChange={(e) => setTargetBrandId(e.target.value)}
                            disabled={moving || brandOptions.length === 0}
                        >
                            {brandOptions.map((brandOption) => (
                                <MenuItem key={brandOption.id} value={brandOption.id}>
                                    {brandOption.name}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    {moveError && (
                        <Alert severity="error" sx={{ mt: 1 }}>{moveError}</Alert>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setConfirmMoveOpen(false)} disabled={moving}>Cancel</Button>
                    <Button onClick={doMove} disabled={moving || brandOptions.length === 0}>
                        {moving ? "Moving..." : "Move"}
                    </Button>
                </DialogActions>
            </Dialog>
            <Dialog open={newLabelOpen} onClose={() => setNewLabelOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Create New Label</DialogTitle>
                <DialogContent>
                    <AddLabelComponent setLoading={setLoading} labelAdded={handleLabelAdd} brand_id={brand_id} />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setNewLabelOpen(false)}>Close</Button>
                </DialogActions>
            </Dialog>
        </React.Fragment>
    );
};

export default ImagePolygonDrawer;
